import { businessToday } from '../Tasks/TaskUtils';
import { expiresAtFrom, isExpired, progressUser } from '../Tasks/progressExpiry';
import { flowLabels, parseFlowKind } from './shiftFlowUtils';

/**
 * Browser-local markers for the flows a trainer has in progress, so the resume
 * bar can appear on every ACC page without a per-page network call.
 *
 * A flow page writes its marker on start and on every step completion, and
 * removes it on finish; opening a flow page reconciles the marker against the
 * server run, which is the real source of truth. A marker is scoped to the
 * trainer who wrote it, the gym and the business day, and carries the time of
 * its last activity: a read drops another trainer's markers (devices are
 * shared), an earlier day's, and anything past the progress TTL, so a shift
 * change never offers to resume the previous trainer's walk.
 */

export const ACTIVE_FLOWS_KEY = 'bbvr_active_flows';
/** Same-tab notification; `storage` only fires in the other tabs. */
export const ACTIVE_FLOWS_EVENT = 'bbvr:active-flows';
export const PAUSE_NOTICE_KEY = 'bbvr_flow_paused';

const readRaw = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(ACTIVE_FLOWS_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const persist = (list, notify = true) => {
    try {
        if (list.length) localStorage.setItem(ACTIVE_FLOWS_KEY, JSON.stringify(list));
        else localStorage.removeItem(ACTIVE_FLOWS_KEY);
    } catch (e) {
        /* ignore storage failures */
    }
    if (notify) {
        try {
            window.dispatchEvent(new Event(ACTIVE_FLOWS_EVENT));
        } catch (e) {
            /* ignore environments without window events */
        }
    }
};

/** When this marker's progress dies, from its last activity. */
export const activeFlowExpiresAt = (entry) => expiresAtFrom(entry?.lastActivityAt);

const isMine = (entry, businessDate) => Boolean(
    entry && parseFlowKind(entry.kind)
    && entry.businessDate === businessDate
    && String(entry.user ?? '') === progressUser()
);

const isFresh = (entry, businessDate, now = Date.now()) =>
    isMine(entry, businessDate) && !isExpired(activeFlowExpiresAt(entry), now);

/** This trainer's live markers. Stale entries are dropped from storage on the way out. */
export const readActiveFlows = (businessDate = businessToday(), now = Date.now()) => {
    const all = readRaw();
    const fresh = all.filter((entry) => isFresh(entry, businessDate, now));
    if (fresh.length !== all.length) persist(fresh, false);
    return fresh;
};

/**
 * Record (or refresh) one flow as in progress. `startedAt` on an existing
 * marker is kept so the bar keeps reporting when the walk actually began, while
 * `lastActivityAt` moves forward and pushes the expiry out with it.
 */
export const markFlowActive = ({ kind, gymId, businessDate, articleTitle = '', startedAt = '' } = {}) => {
    const flowKind = String(kind || '');
    if (!parseFlowKind(flowKind)) return readActiveFlows();
    const day = businessDate || businessToday();
    const now = new Date().toISOString();
    const all = readRaw();
    const previous = all.find((entry) => entry?.kind === flowKind && isMine(entry, day));
    const next = all.filter((entry) => isFresh(entry, day) && entry.kind !== flowKind);
    next.push({
        kind: flowKind,
        user: progressUser(),
        gymId: String(gymId || ''),
        businessDate: day,
        articleTitle: String(articleTitle || previous?.articleTitle || ''),
        startedAt: startedAt || previous?.startedAt || now,
        lastActivityAt: now,
    });
    persist(next);
    return next;
};

export const clearFlowActive = (kind) => {
    const flowKind = String(kind || '');
    const all = readRaw();
    const next = all.filter((entry) => entry?.kind !== flowKind);
    if (next.length !== all.length) persist(next);
    return next;
};

/** 'Closing in progress' / 'Cleaning in progress' / 'Walkthrough: Gym Closing'. */
export const activeFlowLabel = (entry) => {
    const parsed = parseFlowKind(entry?.kind);
    if (!parsed) return '';
    if (parsed.type === 'sop') return `Walkthrough: ${entry.articleTitle || 'SOP'}`;
    return `${flowLabels(entry.kind).title} in progress`;
};

export const activeFlowPath = (entry) => {
    const parsed = parseFlowKind(entry?.kind);
    if (!parsed) return '';
    if (parsed.type === 'sop') return `/shift-flow/sop/${encodeURIComponent(parsed.articleId)}`;
    return `/shift-flow/${parsed.type === 'phase' ? parsed.phase : parsed.type}`;
};

/** Set by Pause so the next page says where to pick the flow back up. */
export const setPauseNotice = () => {
    try {
        sessionStorage.setItem(PAUSE_NOTICE_KEY, 'true');
    } catch (e) {
        /* ignore storage failures */
    }
};

/**
 * Reading and consuming the notice are separate on purpose: a destructive read
 * during render is eaten by StrictMode's throwaway first pass, so the reader
 * checks it and clears it only once it is on screen.
 */
export const hasPauseNotice = () => {
    try {
        return sessionStorage.getItem(PAUSE_NOTICE_KEY) === 'true';
    } catch (e) {
        return false;
    }
};

export const clearPauseNotice = () => {
    try {
        sessionStorage.removeItem(PAUSE_NOTICE_KEY);
    } catch (e) {
        /* ignore storage failures */
    }
};
